import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { extractErrorMessage } from '../../shared/utils/errors';
import { PaginatedResponse, PoolWallet } from '../../shared/types';
import { formatNetworkLabel } from '../../shared/utils/networkLabel';
import { POOL_CURRENCIES } from '../../shared/utils/walletAddress';
import { PoolWalletFilters, deletePoolWallet, listPoolWallets } from './api';

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

export const shortKid = (kid: string): string =>
  kid.length > 14 ? `${kid.slice(0, 8)}…${kid.slice(-4)}` : kid;

interface PoolWalletsSectionProps {
  canManage: boolean;
  onImportClick: () => void;
  refreshKey?: number;
}

/**
 * Unassigned deposit addresses. Addresses are immutable here: they can only
 * enter through a signed batch import and can only be removed, not edited.
 */
const PoolWalletsSection: React.FC<PoolWalletsSectionProps> = ({
  canManage,
  onImportClick,
  refreshKey = 0,
}) => {
  const [pool, setPool] = useState<PaginatedResponse<PoolWallet> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<PoolWalletFilters>({ currency: 'USDT' });
  const [draftFilters, setDraftFilters] = useState<PoolWalletFilters>({ currency: 'USDT' });

  const [deleteTarget, setDeleteTarget] = useState<PoolWallet | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async (page: number, filtersToUse: PoolWalletFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listPoolWallets({ ...filtersToUse, page, per_page: 20 });
      setPool(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load pool wallets'));
      setPool(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load, refreshKey]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    const empty: PoolWalletFilters = { currency: 'USDT' };
    setDraftFilters(empty);
    setFilters(empty);
    setCurrentPage(1);
  };

  const openDelete = (wallet: PoolWallet) => {
    setDeleteTarget(wallet);
    setDeleteError(null);
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      await deletePoolWallet(deleteTarget.id);
      closeDelete();
      load(currentPage, filters);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, 'Failed to delete wallet'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <>
      <div className="card p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
          <select
            value={draftFilters.currency ?? ''}
            onChange={(e) =>
              setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
            }
            className="input py-2"
          >
            <option value="">All</option>
            {POOL_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Address</label>
          <input
            type="text"
            value={draftFilters.address ?? ''}
            onChange={(e) =>
              setDraftFilters((p) => ({ ...p, address: e.target.value || undefined }))
            }
            className="input py-2 font-mono text-xs"
            placeholder="T…"
          />
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
            Apply
          </button>
          <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
            Clear
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : !pool?.data.length ? (
          <div className="p-12 text-center text-slate-400 space-y-3">
            <p>No unassigned wallets in the pool.</p>
            {canManage && (
              <button type="button" onClick={onImportClick} className="btn btn-primary">
                Import signed addresses
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="table-header">ID</th>
                    <th className="table-header">Currency</th>
                    <th className="table-header">Network</th>
                    <th className="table-header">Address</th>
                    <th className="table-header">Signer</th>
                    <th className="table-header">Added</th>
                    {canManage && <th className="table-header text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {pool.data.map((wallet) => (
                    <tr key={wallet.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="table-cell font-mono text-xs text-slate-500">#{wallet.id}</td>
                      <td className="table-cell text-white font-medium">{wallet.currency}</td>
                      <td className="table-cell text-slate-300">{formatNetworkLabel(wallet.network)}</td>
                      <td
                        className="table-cell font-mono text-xs text-slate-300 max-w-[240px] truncate"
                        title={wallet.address}
                      >
                        {wallet.address}
                      </td>
                      <td className="table-cell text-xs whitespace-nowrap">
                        {wallet.signer_kid ? (
                          <span
                            className="text-slate-300"
                            title={`${wallet.signer_kid} · batch #${wallet.batch_id}`}
                          >
                            {wallet.signer_label || shortKid(wallet.signer_kid)}
                            <span className="text-slate-500 ml-1">#{wallet.batch_id}</span>
                          </span>
                        ) : (
                          <span className="badge badge-warning text-[10px]" title="Added before signed imports were required">
                            Unsigned
                          </span>
                        )}
                      </td>
                      <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                        {formatDateTime(wallet.created_at)}
                      </td>
                      {canManage && (
                        <td className="table-cell text-right">
                          <button
                            type="button"
                            onClick={() => openDelete(wallet)}
                            className="btn btn-secondary py-1 px-3 text-xs text-red-400 hover:text-red-300"
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pool.last_page > 1 && (
              <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                <p className="text-xs text-slate-500">
                  {pool.total} wallet{pool.total === 1 ? '' : 's'} in pool
                </p>
                <Pagination
                  currentPage={pool.current_page}
                  lastPage={pool.last_page}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={deleteTarget !== null}
        onClose={closeDelete}
        title="Delete pool wallet?"
        size="md"
      >
        {deleteTarget && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              This will permanently remove the wallet from the deposit pool. This action cannot be
              undone.
            </p>
            <div className="card p-3 bg-slate-800/40 text-xs space-y-1">
              <p>
                <span className="text-slate-500">ID:</span>{' '}
                <span className="font-mono text-slate-200">#{deleteTarget.id}</span>
              </p>
              <p>
                <span className="text-slate-500">Address:</span>{' '}
                <span className="font-mono text-slate-200 break-all">{deleteTarget.address}</span>
              </p>
            </div>
            <p className="text-xs text-amber-400/90">
              Only unassigned pool wallets can be deleted. Assigned wallets are not shown here.
            </p>

            {deleteError && <p className="text-sm text-red-400">{deleteError}</p>}

            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={closeDelete}
                disabled={deleteSubmitting}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteSubmitting}
                className="btn bg-red-600 hover:bg-red-500 text-white disabled:opacity-60"
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete wallet'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default PoolWalletsSection;
